import { useMemo } from "react";
import { useAppSelector } from "../store/hooks";
import { Portal, Category, Lesson } from "../store/slices/storeSlice";
import { getAllowedCategoriesForDisplay, isAssistant } from "./utils";

/**
 * Hook to filter portal data (lessons and categories) based on assistant permissions
 * For teachers/admins: returns all data
 * For assistants: returns only data from categories they have ANY permission for
 *
 * IMPORTANT: This shows categories where assistant has ANY permission, but you still need
 * to check specific permissions (canPublishLessons, canEditLessons, etc.) before allowing actions
 */
export const useFilteredPortalData = () => {
  const { portal, user } = useAppSelector((state) => state.store);

  const filteredData = useMemo(() => {
    if (!portal) {
      return { filteredCats: [], filteredLessons: [] };
    }

    // If not an assistant, return all data
    if (!isAssistant()) {
      return {
        filteredCats: portal.cats || [],
        filteredLessons: portal.data || [],
      };
    }

    // Get allowed categories for display (categories with ANY permission)
    const allowedCategoryIds = getAllowedCategoriesForDisplay();

    // If empty array, assistant has no permissions
    if (!allowedCategoryIds || allowedCategoryIds.length === 0) {
      return { filteredCats: [], filteredLessons: [] };
    }

    // Filter categories - include both main categories and their children
    const filteredCats = portal.cats?.filter((cat: Category) => {
      // Include if the category is in the allowed list
      if (allowedCategoryIds.includes(cat.term_id)) {
        return true;
      }
      // Include if the parent category is in the allowed list
      if (cat.parent && allowedCategoryIds.includes(cat.parent)) {
        return true;
      }
      return false;
    }) || [];

    // Get all allowed category IDs including children
    const allAllowedCategoryIds = filteredCats.map(cat => cat.term_id);

    // Filter lessons - only include lessons from allowed categories
    const filteredLessons = portal.data?.filter((lesson: Lesson) => {
      return allAllowedCategoryIds.includes(lesson.term_taxonomy_id);
    }) || [];

    return { filteredCats, filteredLessons };
  }, [portal, user]);

  return filteredData;
};

/**
 * Hook to check if a specific category is accessible by the current user
 * This checks if the user has ANY permission for this category (not a specific permission type)
 */
export const useCanAccessCategory = (categoryId: number) => {
  const allowedCategories = getAllowedCategoriesForDisplay();

  // If null, user is teacher/admin and can access all
  if (allowedCategories === null) {
    return true;
  }

  // Check if the category is in the allowed list
  return allowedCategories.includes(categoryId);
};

/**
 * Hook to check if a specific lesson is accessible by the current user
 */
export const useCanAccessLesson = (lessonCategoryId: number) => {
  const { filteredCats } = useFilteredPortalData();

  // Check if the lesson's category is in the filtered categories
  return filteredCats.some(cat => cat.term_id === lessonCategoryId);
};
